import { useState, useEffect, useCallback } from "react";
import { UserFeedbackSubmission } from "src/types/userFeedback";
import { getInstanceOfUserFeedbackAPIService } from "src/dependencies";
import { ValidationErrors } from "src/errors";

interface UseFeedbackReturn {
  isFeedbackModalOpen: boolean;
  isLoading: boolean;
  validationErrors?: ValidationErrors<keyof UserFeedbackSubmission>;
  formLvlErrorMessage?: string;
  submitFeedback: (feedbackData: UserFeedbackSubmission) => Promise<void>;
}

export const useFeedback = (): UseFeedbackReturn => {
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFeedbackBeenChecked, setHasFeedbackBeenChecked] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors<keyof UserFeedbackSubmission>>();
  const [formLvlErrorMessage, setFormLvlErrorMessage] = useState<string>();

  const userFeedbackAPIService = getInstanceOfUserFeedbackAPIService();

  const checkFeedbackStatus = useCallback(async () => {
    if (hasFeedbackBeenChecked) return;

    try {
      const response = await userFeedbackAPIService.getUserFeedback();

      // If no feedback exists, show the modal
      if (!response.userFeedback) {
        setIsFeedbackModalOpen(true);
      }

      setHasFeedbackBeenChecked(true);
    } catch (error) {
      console.error("Error checking feedback status:", error);
      setHasFeedbackBeenChecked(true);
    }
  }, [hasFeedbackBeenChecked, userFeedbackAPIService]);

  const submitFeedback = async (feedbackData: UserFeedbackSubmission) => {
    setIsLoading(true);
    setValidationErrors(undefined);
    setFormLvlErrorMessage(undefined);

    try {
      await userFeedbackAPIService.submitUserFeedback(feedbackData);
      setIsFeedbackModalOpen(false);
    } catch (error: any) {
      if (error instanceof ValidationErrors) {
        setValidationErrors(error);
      } else {
        setFormLvlErrorMessage(error?.message || "An error occurred while submitting your feedback. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkFeedbackStatus();
  }, [checkFeedbackStatus]);

  return {
    isFeedbackModalOpen,
    isLoading,
    validationErrors,
    formLvlErrorMessage,
    submitFeedback
  };
};
